<template>
  <SectionHeader
    :show-content="defaultPassThroughBidShowContents"
    :widget-title="$t('HEADER_DEFAULT_PASSTHROUGH_SETTINGS')"
    @onShowContentsClick="onDefaultPassThroughBidShowContentsClick()"
  >
    <template #content>
      <form
        role="form"
        class="form-horizontal"
        name="vm.campaignForm"
      >
        <div v-if="overridesAvailable && (currentPassThroughBid && currentPassThroughBid.enabled)">
          <Field
            :label="$t('LABEL_OVERRIDE_DEFAULT_PASSTHROUGH_SETTINGS')"
            :hide-label="false"
            :has-tooltip="true"
            :tooltip-text="$t('TOOLTIP_OVERRIDE_DEFAULT_PASSTHROUGH_SETTINGS')"
          >
            <template #element>
              <div class="p-l-0 col-sm-5 col-md-1">
                <ValidationProvider slim>
                  <nzToggle
                    v-model="overrideDefaultPassThroughBid"
                    data-cy="default-passthrough-settings-overide-toggle"
                    @onToggle="overrideDefaultPassThroughBidToggle"
                  />
                </ValidationProvider>
              </div>
            </template>
          </Field>
        </div>
        <p
          v-if="overridesAvailable && (currentPassThroughBid && !currentPassThroughBid.enabled)"
          v-t="'PASSTHROUGH_INTRO_FOR_DISABLED_PUB'"
        />

        <div v-if="!overridesAvailable || overrideDefaultPassThroughBid">
          <Field
            v-if="!overridesAvailable"
            :label="$t('LABEL_ENABLE_PASSTHROUGH_SETTINGS')"
            :hide-label="false"
            :has-tooltip="true"
            :tooltip-text="$t('TOOLTIP_ENABLE_PASSTHROUGH_SETTINGS')"
          >
            <template #element>
              <div class="p-l-0 col-sm-5 col-md-1">
                <ValidationProvider slim>
                  <nzToggle
                    v-model="passThroughBid.enabled"
                    data-cy="default-passthrough-bid-toggle"
                    @onToggle="enablePassThroughBidToggle"
                  />
                </ValidationProvider>
              </div>
            </template>
          </Field>
          <div v-if="currentPassThroughBid.enabled || (overrideDefaultPassThroughBid && currentPassThroughBid.enabled)">
            <Field
              :label="$t('LABEL_MAX_BID')"
              :hide-label="false"
              :has-tooltip="true"
              :tooltip-text="$t('TOOLTIP_MAX_BID')"
            >
              <template #element>
                <ValidationProvider
                  v-slot="{ classes, errors }"
                  rules="required|min_value:0.01"
                  slim
                >
                  <div class="p-l-0 col-sm-5 col-md-1">
                    <input
                      v-model="passThroughBid.maxBid"
                      name="maxBid"
                      data-cy="passThroughMaxBid"
                      :required="true"
                      class="form-control"
                      :class="classes"
                      type="number"
                      @change="updatePassThroughBid"
                      @wheel="$event.target.blur()"
                    >
                  </div>
                  <label
                    v-if="errors[0]"
                    name="passThroughMaxBid-invalid-label"
                    data-cy="passThroughMaxBid-invalid-label"
                    class="form-group__label-required vv-invalid"
                  >{{ $t('ERROR_MAX_BID_AMOUNT') }}
                  </label>
                  <label
                    v-else
                    class="form-group__label-required"
                  >{{ $t('REQUIRED') }}
                  </label>
                </ValidationProvider>
              </template>
            </Field>
            <Field
              :label="$t('LABEL_ALLOW_BIDS_AT_LOSS')"
              :hide-label="false"
              :has-tooltip="true"
              :tooltip-text="$t('TOOLTIP_ALLOW_BIDS_AT_LOSS')"
            >
              <template #element>
                <div class="p-l-0 col-sm-5 col-md-1">
                  <ValidationProvider slim>
                    <nzToggle
                      v-model="passThroughBid.allowBidsAtLoss"
                      data-cy="passThroughAllowBidsAtLoss"
                      @onToggle="allowBidsAtLossToggle($event)"
                    />
                  </ValidationProvider>
                </div>
              </template>
            </Field>
            <Field
              :label="$t('LABEL_PASS_THROUGH_TYPE')"
              :hide-label="false"
              :has-tooltip="false"
            >
              <template #element>
                <div class="p-l-0 col-sm-6 col-md-6">
                  <ValidationProvider slim>
                    <MultipleToggle
                      data-cy="passThroughBidMarginType"
                      :value="passThroughBidType"
                      :options="passThroughBidTypeOptions"
                      @onToggle="passThroughBidTypeChange($event)"
                    />
                  </ValidationProvider>
                </div>
              </template>
            </Field>
            <template v-if="passThroughBidType === 'percentage'">
              <Field
                :label="$t('LABEL_BID_MARGIN_PERCENTAGE')"
                :hide-label="false"
                :has-tooltip="true"
                :tooltip-text="$t('TOOLTIP_BID_MARGIN_PERCENTAGE')"
              >
                <template #element>
                  <ValidationProvider
                    v-slot="{ classes, errors }"
                    rules="min_value:0"
                    slim
                  >
                    <div class="p-l-0 col-sm-5 col-md-1">
                      <input
                        v-model="passThroughBid.bidMarginPercentage"
                        name="bidMarginPercentage"
                        data-cy="passThroughBidMarginPercentage"
                        :required="false"
                        :placeholder="$t('LABEL_PLACEHOLDER_MIN_BID_MARGIN_PERCENTAGE')"
                        class="form-control text-align-right"
                        :class="classes"
                        type="number"
                        @change="updatePassThroughBid"
                        @wheel="$event.target.blur()"
                      >
                    </div>
                    <label
                      v-if="errors[0]"
                      name="passThroughBidMarginPercentage-invalid-label"
                      data-cy="passThroughBidMarginPercentage-invalid-label"
                      class="form-group__label-required vv-invalid"
                    >{{ $t('ERROR_GREATER_THAN_OR_EQUAL') }}
                    </label>
                  </ValidationProvider>
                </template>
              </Field>
              <Field
                :label="$t('LABEL_MIN_BID_MARGIN_PERCENTAGE_AFTER_ADJUSTMENTS')"
                :hide-label="false"
                :has-tooltip="true"
                :tooltip-text="$t('TOOLTIP_MIN_BID_MARGIN_PERCENTAGE_AFTER_ADJUSTMENTS')"
              >
                <template #element>
                  <ValidationProvider
                    v-slot="{ classes, errors }"
                    rules="min_value:0"
                    slim
                  >
                    <div class="p-l-0 col-sm-5 col-md-1">
                      <input
                        v-model="passThroughBid.minBidMarginPercentageAfterAdjustments"
                        name="minBidMarginPercentageAfterAdjustments"
                        data-cy="passThroughMinBidMarginPercentageAfterAdjustments"
                        :required="false"
                        :placeholder="$t('LABEL_PLACEHOLDER_MIN_BID_MARGIN_PERCENTAGE_AFTER_ADJUSTMENTS')"
                        class="form-control text-align-right"
                        :class="classes"
                        type="number"
                        @change="updatePassThroughBid"
                        @wheel="$event.target.blur()"
                      >
                    </div>
                    <label
                      v-if="errors[0]"
                      name="passThroughMinBidMarginPercentageAfterAdjustments-invalid-label"
                      data-cy="passThroughMinBidMarginPercentageAfterAdjustments-invalid-label"
                      class="form-group__label-required vv-invalid"
                    >{{ $t('ERROR_GREATER_THAN_OR_EQUAL') }}
                    </label>
                  </ValidationProvider>
                </template>
              </Field>
            </template>
            <template v-if="passThroughBidType === 'amount'">
              <Field
                :label="$t('LABEL_BID_MARGIN_AMOUNT')"
                :hide-label="false"
                :has-tooltip="true"
                :tooltip-text="$t('TOOLTIP_BID_MARGIN_AMOUNT')"
              >
                <template #element>
                  <ValidationProvider
                    v-slot="{ classes, errors }"
                    rules="min_value:0"
                    slim
                  >
                    <div class="p-l-0 col-sm-5 col-md-1">
                      <input
                        v-model="passThroughBid.bidMarginAmount"
                        name="bidMarginAmount"
                        data-cy="passThroughBidMarginAmount"
                        :required="false"
                        :placeholder="$t('LABEL_PLACEHOLDER_MIN_BID_MARGIN_AMOUNT')"
                        class="form-control text-align-right"
                        :class="classes"
                        type="number"
                        @change="updatePassThroughBid"
                        @wheel="$event.target.blur()"
                      >
                    </div>
                    <label
                      v-if="errors[0]"
                      name="passThroughBidMarginAmount-invalid-label"
                      data-cy="passThroughBidMarginAmount-invalid-label"
                      class="form-group__label-required vv-invalid"
                    >{{ $t('ERROR_GREATER_THAN_OR_EQUAL') }}
                    </label>
                  </ValidationProvider>
                </template>
              </Field>
              <Field
                :label="$t('LABEL_MIN_BID_MARGIN_AMOUNT_AFTER_ADJUSTMENTS')"
                :hide-label="false"
                :has-tooltip="true"
                :tooltip-text="$t('TOOLTIP_MIN_BID_MARGIN_AMOUNT_AFTER_ADJUSTMENTS')"
              >
                <template #element>
                  <ValidationProvider
                    v-slot="{ classes, errors }"
                    rules="min_value:0"
                    slim
                  >
                    <div class="p-l-0 col-sm-5 col-md-1">
                      <input
                        v-model="passThroughBid.minBidMarginAmountAfterAdjustments"
                        name="minBidMarginAmountAfterAdjustments"
                        data-cy="passThroughMinBidMarginAmountAfterAdjustments"
                        :required="false"
                        :placeholder="$t('LABEL_PLACEHOLDER_MIN_BID_MARGIN_AMOUNT_AFTER_ADJUSTMENTS')"
                        class="form-control text-align-right"
                        :class="classes"
                        type="number"
                        @change="updatePassThroughBid"
                        @wheel="$event.target.blur()"
                      >
                    </div>
                    <label
                      v-if="errors[0]"
                      name="passThroughMinBidMarginAmountAfterAdjustments-invalid-label"
                      data-cy="passThroughMinBidMarginAmountAfterAdjustments-invalid-label"
                      class="form-group__label-required vv-invalid"
                    >{{ $t('ERROR_GREATER_THAN_OR_EQUAL') }}
                    </label>
                  </ValidationProvider>
                </template>
              </Field>
            </template>
            <Field
              :label="$t('LABEL_MIN_BID_DURATION_SELECTION')"
              :hide-label="false"
              :has-tooltip="true"
              :tooltip-text="$t('TOOLTIP_MIN_BID_DURATION_SELECTION')"
            >
              <template #element>
                <ValidationProvider
                  v-slot="{ classes }"
                  rules="required"
                  slim
                >
                  <div class="p-l-0 col-sm-4 col-md-3">
                    <SearchableDropdown
                      v-model="selectedBidDuration"
                      name="bidDuration"
                      class="rtb-default-selector m-b-10"
                      :class="classes"
                      :config="bidDurationDropdownConfig"
                      :searchable="false"
                      :clearable="false"
                      @change="onBidDurationDropdownOnChange"
                    />
                  </div>
                </ValidationProvider>
              </template>
            </Field>
            <template v-if="bidDuration === 'Custom'">
              <div class="col-sm-offset-3 col-md-offset-3 col-md-3 col-sm-4 p-l-0">
                <div class="bs-callout bs-callout-sm bs-callout-warning">
                  <p v-t="'CUSTOM_BID_DURATION_WARNING'" />
                </div>
              </div>
            </template>
            <template v-if="bidDuration === 'Static'">
              <Field
                :label="$t('LABEL_MIN_BID_STATIC')"
                :hide-label="false"
                :has-tooltip="true"
                :tooltip-text="$t('TOOLTIP_MIN_BID_STATIC')"
              >
                <template #element>
                  <ValidationProvider
                    v-slot="{ classes, errors }"
                    rules="required|min_value:0.01"
                    slim
                  >
                    <div class="p-l-0 col-sm-5 col-md-1">
                      <input
                        v-model="bidDurationAdjustmentAmount"
                        name="bidDurationAdjustmentAmount"
                        data-cy="bidDurationAdjustmentAmount"
                        :required="false"
                        :class="classes"
                        class="form-control text-align-right"
                        type="number"
                        @input="updateBidDuration"
                        @wheel="$event.target.blur()"
                      >
                    </div>
                    <label
                      v-t="'SECONDS_LABEL'"
                      class="m-t-10"
                    />
                    <label
                      v-if="errors[0]"
                      name="bidDurationAdjustmentAmount-invalid-label"
                      data-cy="bidDurationAdjustmentAmount-invalid-label"
                      class="form-group__label-required vv-invalid"
                    >{{ $t('ERROR_BID_STATIC_ADJUSTMENT_AMOUNT') }}
                    </label>
                  </ValidationProvider>
                </template>
              </Field>
            </template>
            <template v-if="bidDuration === 'Adjusted'">
              <Field
                :label="$t('LABEL_MIN_BID_ADJUST')"
                :hide-label="false"
                :has-tooltip="true"
                :tooltip-text="$t('TOOLTIP_MIN_BID_ADJUST')"
              >
                <template #element>
                  <ValidationProvider
                    v-slot="{ classes, errors }"
                    rules="required|min_value:0.01"
                    slim
                  >
                    <div class="p-l-0 col-sm-5 col-md-1">
                      <input
                        v-model="bidDurationAdjustmentAmount"
                        name="bidDurationAdjustmentAmount"
                        data-cy="bidDurationAdjustmentAmount"
                        :required="false"
                        class="form-control text-align-right"
                        :class="classes"
                        type="number"
                        @input="updateBidDuration"
                        @wheel="$event.target.blur()"
                      >
                    </div>
                    <label
                      v-t="'SECONDS_LABEL'"
                      class="m-t-10"
                    />
                    <label
                      v-if="errors[0]"
                      name="bidDurationAdjustmentAmount-invalid-label"
                      data-cy="bidDurationAdjustmentAmount-invalid-label"
                      class="form-group__label-required vv-invalid"
                    >{{ $t('ERROR_BID_ADJUSTED_ADJUSTMENT_AMOUNT') }}
                    </label>
                  </ValidationProvider>
                </template>
              </Field>
            </template>
          </div>
        </div>
      </form>
    </template>
  </SectionHeader>
</template>

<script>
import messages from '../locales';
import sharedMessages from '@/core/locales';

import SectionHeader from '@/core/components/SectionHeader/SectionHeader';
import Field from '@/core/components/LabelAndElement/LabelAndElement';
import nzToggle from '@/core/components/nzToggle/nzToggle';
import MultipleToggle from '@/core/components/MultipleToggle/MultipleToggle';
import SearchableDropdown from '@/core/components/SearchableDropdown/SearchableDropdown';
import { comparisonTypes } from '../../../../data/filterData';

export default {
  name: 'DefaultPassThroughBid',
  components: {
    SectionHeader,
    Field,
    nzToggle,
    MultipleToggle,
    SearchableDropdown
  },
  props:{
    enablePassThroughBid: {
      type: Boolean,
      default: () => {
        return false;
      }
    },
    overridesAvailable: {
      type: Boolean,
      default: () => {
        return false;
      }
    },
    currentPassThroughBid: {
      type: Object,
      default: () => {
        return {
          enabled: false,
          allowBidsAtLoss: false
        };
      }
    },
    overrideDefaultPassThroughBid: {
      type: Boolean,
      default: () => {
        return false;
      }
    },
    currentPassThroughBidOverride: {
      type: Object,
      default: () => {
        return null;
      }
    },
    currentMinDurationAdjustments: {
      type: Array,
      default: () => {
        return [];
      }
    },
    currentMinDurationAdjustmentsOverride: {
      type: Array,
      default: () => {
        return [];
      }
    },
  },
  data() {
    return {
      passThroughBid: this.overridesAvailable ? this.currentPassThroughBidOverride : this.currentPassThroughBid,
      minDurationAdjustments: this.overridesAvailable ? this.currentMinDurationAdjustmentsOverride : this.currentMinDurationAdjustments,
      defaultPassThroughBidShowContents: true,
      passThroughBidTypeOptions: [
        {
          name: this.$t('LABEL_PERCENTAGE'),
          value: 'percentage'
        },
        {
          name: this.$t('LABEL_AMOUNT'),
          value: 'amount'
        }
      ],
      bidDuration: 'Default',
      bidDurationDropdownConfig: {
        options: [
          { label: this.$t('DROPDOWN_BID_DURATION_OPTION_DEFAULT'), id: 'Default' },
          { label: this.$t('DROPDOWN_BID_DURATION_OPTION_STATIC'), id: 'Static' },
          { label: this.$t('DROPDOWN_BID_DURATION_OPTION_ADJUSTED'), id: 'Adjusted' },
        ],
        placeholder: 'Default'
      },
      bidDurationAdjustmentAmount: 0,
    };
  },
  computed: {
    selectedBidDuration: {
      get() {
        return this.bidDurationDropdownConfig.options.find(({ id }) => id === this.bidDuration) ?? 'Custom';
      },
      set(value) {
        this.bidDuration = value.id;
        this.updateBidDuration();
      }
    }
  },
  watch: {
    minDurationAdjustments: function (newValue) {
      this._processMinDurationAdjustments(newValue);
    }
  },
  created() {
    if (this.passThroughBid?.bidMarginPercentage != null || this.passThroughBid?.minBidMarginPercentageAfterAdjustments != null) {
      this.passThroughBidType = 'percentage';
    } else if (this.passThroughBid?.bidMarginAmount != null || this.passThroughBid?.minBidMarginAmountAfterAdjustments != null) {
      this.passThroughBidType = 'amount';
    } else {
      this.passThroughBidType = 'percentage';
    }
    this._processMinDurationAdjustments(this.minDurationAdjustments);
  },
  methods: {
    overrideDefaultPassThroughBidToggle(val) {
      this.$emit('overrideDefaultPassThroughBidToggle', val);
    },
    enablePassThroughBidToggle(val) {
      this.$emit('enablePassThroughBidToggle', val);
    },
    updatePassThroughBid() {
      this.$emit('updatePassThroughBid', this.passThroughBid);
    },
    onBidDurationDropdownOnChange(val) {
      this.bidDuration = val.id;
      this.updateBidDuration();
    },
    onDefaultPassThroughBidShowContentsClick() {
      this.defaultPassThroughBidShowContents = !this.defaultPassThroughBidShowContents;
    },
    allowBidsAtLossToggle(val) {
      this.passThroughBid.allowBidsAtLoss = val;
      this.updatePassThroughBid();
    },
    passThroughBidTypeChange(event) {
      this.passThroughBidType = event.value;
      if (event.value === 'amount') {
        this.passThroughBid.bidMarginPercentage = '';
        this.passThroughBid.minBidMarginPercentageAfterAdjustments = '';
      }
      if (event.value === 'percentage') {
        this.passThroughBid.bidMarginAmount = '';
        this.passThroughBid.minBidMarginAmountAfterAdjustments = '';
      }
      this.updatePassThroughBid();
    },
    updateBidDuration() {
      if (this.bidDuration == 'Default') {
        this.$emit('updateMinDuration', []);
      } else {
        if (this.bidDuration == 'Static') {
          this.$emit('updateMinDuration', [{
            criteria: {
              tagId: '',
              comparisonType: 'ALWAYS',
              value: '',
              isNegativeMatch: false
            },
            adjustmentType: 'Override',
            adjustmentValue: this.bidDurationAdjustmentAmount
          }]);
        }
        if (this.bidDuration == 'Adjusted') {
          this.$emit('updateMinDuration', [{
            criteria: {
              tagId: '',
              comparisonType: 'ALWAYS',
              value: '',
              isNegativeMatch: false
            },
            adjustmentType: 'Add',
            adjustmentValue: this.bidDurationAdjustmentAmount
          }]);
        }
      }
    },

    // This method processes the min duration adjustments and sets the bid duration accordingly for display and request purposes.
    _processMinDurationAdjustments(val) {
      this.bidDuration = 'Custom';
      if (val == null || val.length == 0) {
        this.bidDuration = 'Default';
      }
      else
      {
        if (val.length == 1) {
          const durationAdjustment = val[0];
          const criteria = durationAdjustment?.criteria;
          if (criteria && criteria.comparisonType == 'ALWAYS' && criteria.isNegativeMatch == false && criteria.tagId == '' && criteria.value == '')
          {
            if (durationAdjustment.adjustmentType == 'Override') {
              this.bidDuration = 'Static';
              this.bidDurationAdjustmentAmount = durationAdjustment.adjustmentValue ?? 0;
            }
            if (durationAdjustment.adjustmentType == 'Add') {
              this.bidDuration = 'Adjusted';
              this.bidDurationAdjustmentAmount = Math.abs(durationAdjustment.adjustmentValue ?? 0);
            }
          }
        }
      }
    }
  },
  i18n: {
    messages,
    sharedMessages,
    locale: 'en'
  },
};
</script>